import { useEffect, useState } from 'react';
import { useMutation } from '@apollo/client';
import Box from '@carrier-io/air-react/Box';
import Button from '@carrier-io/air-react/Button';
import ModalContent from '@carrier-io/air-react/ModalContent';
import ModalActions from '@carrier-io/air-react/ModalActions';
import TextField from '@carrier-io/air-react/TextField';
import { Modal, ModalTitle, Typography } from '@carrier-io/air-react';
import Stack from '@carrier-io/air-react/Stack';
import Checkbox from '@carrier-io/air-react/Checkbox';
import FormControlLabel from '@carrier-io/air-react/FormControlLabel';
import InputAdornment from '@carrier-io/air-react/InputAdornment';
import IconButton from '@carrier-io/air-react/IconButton';
import EmailIcon from 'components/icons/email-icon';
import { ConfirmPopup } from './ConfirmPopup';
import { isIdValid } from 'utils/validators';
import { TextFieldColor } from 'constants/componentEnums';
import { useNotification } from 'context/NotificationContext';
import { VendorRepresentative, VendorRepresentativeInput, VendorRoles } from 'store/models/models';
import { NotificationBgColor } from 'store/models/Notification';
import { UpdatedUserInput } from 'store/models/User';
import { CREATE_VENDOR_REPRESENTATIVE } from 'store/graphql/mutations/CREATE_VENDOR_REPRESENTATIVE';
import { UPDATE_VENDOR_REPRESENTATIVE } from 'store/graphql/mutations/UPDATE_VENDOR_REPRESENTATIVE';
import styles from '../../styles/Popup.module.css';

export type UserPopupProps = {
  opened: boolean;
  onCancel: () => void;
  onUserAdded: () => void;
  isEditing: boolean;
  editableUser: VendorRepresentative | null;
  vendorId: string;
};

export const UserPopup = ({ opened, onCancel, onUserAdded, isEditing, editableUser, vendorId }: UserPopupProps) => {
  const { setIsNotificationOpened, setTextContent, setNotificationBgColor } = useNotification();
  const [confirmPopupOpened, setConfirmPopupOpened] = useState(false);

  // Contact person states
  const [firstName, setFirstName] = useState('');
  const [firstNameError, setFirstNameError] = useState('');
  const [isFirstNameError, setIsFirstNameError] = useState(false);

  const [lastName, setLastName] = useState('');
  const [lastNameError, setLastNameError] = useState('');
  const [isLastNameError, setIsLastNameError] = useState(false);

  const [emailAddress, setEmailAddress] = useState('');
  const [emailAddressError, setEmailAddressError] = useState('');
  const [isEmailAddressError, setIsEmailAddressError] = useState(false);

  const [readOnlyFlag, setReadOnlyFlag] = useState(true);

  const [createVendorRepresentative, { loading: creatingLoading }] = useMutation(CREATE_VENDOR_REPRESENTATIVE);
  const [updateVendorRepresentative, { loading: updatingLoading }] = useMutation(UPDATE_VENDOR_REPRESENTATIVE);
  // Validations for contact person fields
  const validateFirstName = () => {
    setFirstNameError('');
    let isError = false;
    if (firstName.trim().length < 1) {
      setFirstNameError('First name is required');
      isError = true;
    } else if (firstName.length > 30) {
      setFirstNameError('First name should not exceed 30 characters');
      isError = true;
    } else {
      const regex = /^[a-zA-Z\s'-]+$/;
      if (!regex.test(firstName)) {
        setFirstNameError('First name contains invalid characters');
        isError = true;
      }
    }
    setIsFirstNameError(isError);
    return isError;
  };

  const validateLastName = () => {
    setLastNameError('');
    let isError = false;
    if (lastName.trim().length < 1) {
      setLastNameError('Last name is required');
      isError = true;
    } else if (lastName.length > 30) {
      setLastNameError('Last name should not exceed 30 characters');
      isError = true;
    } else {
      const regex = /^[a-zA-Z\s'-]+$/;
      if (!regex.test(lastName)) {
        setLastNameError('Last name contains invalid characters');
        isError = true;
      }
    }
    setIsLastNameError(isError);
    return isError;
  };

  const validateEmailAddress = () => {
    setEmailAddressError('');
    let isError = false;
    if (emailAddress.trim().length < 1) {
      setEmailAddressError('Email address is required');
      isError = true;
    } else {
      const regex = /^\S+@\S+\.\S+$/;
      if (!regex.test(emailAddress)) {
        setEmailAddressError('Invalid email address format');
        isError = true;
      }
    }
    setIsEmailAddressError(isError);
    return isError;
  };

  const resetFields = () => {
    setFirstName('');
    setFirstNameError('');
    setIsFirstNameError(false);

    setLastName('');
    setLastNameError('');
    setIsLastNameError(false);

    setEmailAddress('');
    setEmailAddressError('');
    setIsEmailAddressError(false);

    setReadOnlyFlag(true);
  };

  const closeConfirmPopup = () => {
    setConfirmPopupOpened(false);
  };

  const openConfirmPopup = () => {
    setConfirmPopupOpened(true);
  };

  const handleClose = () => {
    resetFields();
    onCancel();
    closeConfirmPopup();
  };

  const isValidNewUserData = () => {
    return isIdValid(vendorId) && !validateFirstName() && !validateLastName() && !validateEmailAddress();
  };

  const handleCreateUser = async () => {
    const isValidData = isValidNewUserData();

    if (!isValidData) {
      return;
    }

    const userToSave: VendorRepresentativeInput = {
      firstName,
      lastName,
      email: emailAddress,
      readOnly: readOnlyFlag,
    };

    const userName = `${firstName || ''} ${lastName || ''}`;

    await createVendorRepresentative({
      variables: {
        vendorRepresentative: userToSave,
        vendorId,
      },
    })
      .then((response) => {
        if (response.data && !response?.errors?.length) {
          setTextContent(`${userName} has been successfully added`);
          setNotificationBgColor(NotificationBgColor.SUCCESS);
          setIsNotificationOpened(true);
          resetFields();
          onUserAdded();
        }
      })
      .catch((error) => {
        console.error('Error creating user:', error.message);
        if (
          error.message &&
          (error.message.includes('USER_ALREADY_EXISTS') ||
            error.message.includes('User already exists') ||
            error.message.includes('User already exists with another vendor'))
        ) {
          setTextContent(`User with email ${emailAddress} is already in use by a different vendor. Use another email.`);
        } else {
          setTextContent(`There was an issue with your request. Please check the entered data and try again`);
        }
        setNotificationBgColor(NotificationBgColor.ERROR);
        setIsNotificationOpened(true);

        if (error?.message?.includes('email already exists')) {
          setEmailAddressError('Email already exists');
          setIsEmailAddressError(true);
        }
      });
  };

  const isValidUpdatedUserData = () => {
    return !firstNameError && !lastNameError;
  };

  const handleUpdateUser = async () => {
    const isUserDataInvalid = !isValidUpdatedUserData();
    const isFirstNameUpdated = editableUser?.firstName !== firstName;
    const isLastNameUpdated = editableUser?.lastName !== lastName;
    const isNoUserDataChanged = !isFirstNameUpdated && !isLastNameUpdated;

    if (isUserDataInvalid) {
      return;
    } else if (isNoUserDataChanged) {
      setTextContent('No changes have been saved. Please make updates before submitting');
      setNotificationBgColor(NotificationBgColor.WARNING);
      setIsNotificationOpened(true);
    }

    const userToUpdate: UpdatedUserInput = {
      ...(isFirstNameUpdated ? { firstName } : {}),
      ...(isLastNameUpdated ? { lastName } : {}),
    };

    try {
      await updateVendorRepresentative({
        variables: {
          vendorId,
          vendorRepresentativeId: editableUser?.id,
          vendorRepresentativeUpdate: userToUpdate,
        },
      });

      setTextContent(`${firstName} ${lastName} has been successfully updated`);
      setNotificationBgColor(NotificationBgColor.SUCCESS);
      setIsNotificationOpened(true);
      resetFields();
      onUserAdded();
    } catch (error) {
      console.error('Error updating user:', error);
      setTextContent('There was an issue with your request. Please check the entered data and try again');
      setNotificationBgColor(NotificationBgColor.ERROR);
      setIsNotificationOpened(true);
    }
  };

  useEffect(() => {
    if (isEditing && editableUser) {
      const { firstName, lastName, email, roles } = editableUser;

      setFirstName(firstName);
      setLastName(lastName);
      setEmailAddress(email);
      setReadOnlyFlag(roles?.includes(VendorRoles.VENDOR_VIEWER));
    }
  }, [isEditing, opened]);

  return (
    <Modal open={opened} fullWidth maxWidth="sm" scroll="body">
      <ModalTitle
        data-testid="user-modal-title"
        maxWidth="md"
        className={styles.popupTitle}
        clearable
        onClose={openConfirmPopup}
      >
        <Stack direction="row" justifyContent="space-between" width={'100%'}>
          <Typography variant="h6" fontWeight={600}>
            {isEditing ? 'Edit User' : 'Invite a new user'}
          </Typography>
        </Stack>
      </ModalTitle>
      <ModalContent>
        <Box display="flex" flexDirection="column" width="100%">
          <Stack direction="column" spacing={1}>
            <Stack direction="row" justifyContent="space-between" spacing={1}>
              <TextField
                data-testid="user-first-name-form"
                value={firstName}
                required={true}
                label="First Name"
                type="text"
                color={TextFieldColor.Primary}
                showBorder
                onChange={(event) => setFirstName(event.target.value)}
                onBlur={validateFirstName}
                helperText={firstNameError}
                error={isFirstNameError}
                hideBackgroundColor
              />
              <TextField
                data-testid="user-last-name-form"
                value={lastName}
                required={true}
                onChange={(event) => setLastName(event.target.value)}
                onBlur={validateLastName}
                label="Last Name"
                type="text"
                color={TextFieldColor.Primary}
                showBorder
                hideBackgroundColor
                helperText={lastNameError}
                error={isLastNameError}
              />
            </Stack>
            <TextField
              data-testid="user-email-form"
              value={emailAddress}
              className={styles.emailField}
              disabled={isEditing}
              required={true}
              onChange={(event) => setEmailAddress(event.target.value)}
              onBlur={validateEmailAddress}
              label="Email"
              type="text"
              color={TextFieldColor.Primary}
              showBorder
              hideBackgroundColor
              helperText={emailAddressError}
              error={isEmailAddressError}
              inputSetting={{
                endAdornment: (
                  <InputAdornment className={styles.emailIcon} disableTypography position="start" variant="standard">
                    <IconButton>
                      <EmailIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  data-testid="user-read-only-form"
                  disabled={true}
                  color="base"
                  checked={readOnlyFlag}
                  onChange={(event, checked) => setReadOnlyFlag(checked)}
                />
              }
              label="Limit access to read-only"
              labelPlacement="end"
            />
          </Stack>
        </Box>
      </ModalContent>
      <ModalActions className={styles.modalActions}>
        <Button data-testid="user-cancel-form" variant="text" color="secondary" onClick={openConfirmPopup}>
          Cancel
        </Button>
        <Button
          data-testid="user-submit-form"
          disabled={creatingLoading || updatingLoading}
          variant="outlined"
          color="primary"
          onClick={isEditing ? handleUpdateUser : handleCreateUser}
        >
          Save User
        </Button>
      </ModalActions>
      <ConfirmPopup onCancel={closeConfirmPopup} onConfirm={handleClose} opened={confirmPopupOpened} />
    </Modal>
  );
};
